import { useState, useEffect } from "react";
import { C, S, G } from "../utils/theme";
import { usersAPI, commAPI } from "../utils/api";

const ADMIN_NAV = [
  { key:"dashboard", label:"Dashboard", icon:"📊" },
  { section:"ACADEMIA" },
  { key:"degrees", label:"Degrees & Programs", icon:"🎓" },
  { key:"courses", label:"Course Management", icon:"📚" },
  { key:"modules", label:"Modules & Lectures", icon:"🎬" },
  { section:"STUDENTS" },
  { key:"universities", label:"Universities", icon:"🏫" },
  { key:"students", label:"Student Management", icon:"👥" },
  { key:"fees", label:"Fees Management", icon:"💰" },
  { section:"ACADEMICS" },
  { key:"assignments", label:"Assignments", icon:"📝" },
  { key:"exams", label:"Exams & MCQs", icon:"📋" },
  { key:"grades", label:"Grades & Reports", icon:"🏆" },
  { section:"COMMUNICATION" },
  { key:"announcements", label:"Announcements", icon:"📢" },
  { key:"qa_admin", label:"Q&A Management", icon:"💬" },
];

const STUDENT_NAV = [
  { key:"dashboard", label:"Dashboard", icon:"📊" },
  { section:"LEARNING" },
  { key:"my_courses", label:"My Courses", icon:"📚" },
  { key:"lectures_s", label:"Lectures", icon:"🎬" },
  { section:"TASKS" },
  { key:"assignments_s", label:"Assignments", icon:"📝" },
  { key:"exams_s", label:"Exams", icon:"📋" },
  { section:"RESULTS" },
  { key:"grades_s", label:"My Grades", icon:"🏆" },
  { section:"CONNECT" },
  { key:"announcements_s", label:"Announcements", icon:"📢" },
  { key:"qa_s", label:"Q&A / AI Help", icon:"🤖" },
  { section:"RECREATION" },
  { key:"games", label:"Games", icon:"🎮" },
  { section:"ACCOUNT" },
  { key:"settings_s", label:"Change Password", icon:"🔒" },
];

const LABELS = {
  dashboard:"Dashboard", degrees:"Degrees & Programs", courses:"Course Management",
  modules:"Modules & Lectures", universities:"Universities", students:"Student Management", fees:"Fees Management",
  assignments:"Assignments", exams:"Exams & MCQs", grades:"Grades & Reports",
  announcements:"Announcements", qa_admin:"Q&A Management",
  my_courses:"My Courses", lectures_s:"My Lectures", assignments_s:"My Assignments",
  exams_s:"My Exams", grades_s:"My Grades", announcements_s:"Announcements",
  qa_s:"Q&A / AI Help", games:"🎮 Games", settings_s:"Change Password",
};

// Scoped to this file only — inline styles elsewhere can't express @media,
// so this small stylesheet handles just the mobile sidebar toggle behavior.
// Below 768px the sidebar is off-canvas by default and only used here.
const MOBILE_NAV_CSS = `
.lms-hamburger { display: none; }
@media (max-width: 768px) {
  .lms-hamburger { display: inline-flex !important; }
  .lms-sidebar { transform: translateX(-100%); transition: transform 0.25s ease; }
  .lms-sidebar.lms-sidebar-open { transform: translateX(0); box-shadow: 4px 0 24px rgba(15,23,42,0.18); }
  .lms-main { margin-left: 0 !important; }
  .lms-topbar { padding: 10px 16px !important; }
}
`;

export default function Layout({ user, onLogout, page, setPage, children }) {
  const [showNotif, setShowNotif] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const nav = user.role === "admin" ? ADMIN_NAV : STUDENT_NAV;

  // Load notifications
  useEffect(() => {
    const loadNotifs = async () => {
      try {
        const res = await usersAPI.getNotifications();
        setNotifications(res.data || []);
      } catch (err) {}
    };
    loadNotifs();
    // Refresh every 30 seconds
    const interval = setInterval(loadNotifs, 30000);
    return () => clearInterval(interval);
  }, []);

  // Safety net: if the window is resized back to desktop width while the
  // mobile menu happens to be open, close it so the backdrop doesn't linger.
  useEffect(() => {
    const onResize = () => { if (window.innerWidth > 768) setMobileOpen(false); };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const unread = notifications.filter(n => !n.is_read).length;

  const markAllRead = async () => {
    try {
      await usersAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: 1 })));
    } catch (err) {}
  };

  return (
    <div style={S.layout}>
      <style>{MOBILE_NAV_CSS}</style>

      {/* Sidebar */}
      <div className={`lms-sidebar${mobileOpen ? " lms-sidebar-open" : ""}`} style={S.sidebar}>
        {/* Logo */}
        <div style={{ padding:"24px 22px 18px", borderBottom:`1px solid ${C.border}` }}>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <img src={user.university_logo || "/logo.svg"} alt={user.university_name || "Online Learning"} style={{ width:42, height:42, objectFit:"contain" }} />
            <div>
              <div style={{ fontSize:15, fontWeight:800, color:C.text, letterSpacing:-0.3 }}>{user.university_name || "Online Learning"}</div>
              <div style={{ fontSize:10, color:C.textMuted, textTransform:"uppercase", letterSpacing:1 }}>LMS System</div>
            </div>
          </div>
        </div>

        {/* User Info */}
        <div style={{ margin:"12px 12px 4px", padding:"12px 14px", background:C.surfaceAlt, borderRadius:12, border:`1px solid ${C.border}` }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ width:38, height:38, borderRadius:"50%", background:user.role==="admin"?G.blue:G.purple, display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, flexShrink:0, overflow:"hidden" }}>
              {user.photo
                ? <img src={user.photo} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                : (user.role==="admin" ? "👨‍💼" : "👨‍🎓")
              }
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:13, fontWeight:700, color:C.text, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{user.name}</div>
              <div style={{ fontSize:10, color:C.textMuted, marginTop:1 }}>
                {user.role==="admin" ? "Administrator" : user.student_id || user.email}
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav style={{ flex:1, padding:"8px 10px", overflowY:"auto" }}>
          {nav.map((item, i) =>
            item.section
              ? <div key={i} style={{ fontSize:9, fontWeight:700, letterSpacing:1.5, color:C.textFaint, padding:"14px 12px 4px", textTransform:"uppercase" }}>{item.section}</div>
              : <button key={item.key} onClick={() => { setPage(item.key); setMobileOpen(false); }} style={{
                  ...S.btn("ghost","md"),
                  width:"100%", justifyContent:"flex-start", padding:"10px 14px",
                  borderRadius:10, marginBottom:2, fontSize:13,
                  background: page===item.key ? C.navyPale : "transparent",
                  color: page===item.key ? C.navy : C.textMuted,
                  borderLeft: page===item.key ? `3px solid ${C.navy}` : "3px solid transparent",
                  fontWeight: page===item.key ? 700 : 400,
                }}>
                  <span style={{ fontSize:15, marginRight:2 }}>{item.icon}</span> {item.label}
                </button>
          )}
        </nav>

        {/* Logout */}
        <div style={{ padding:"12px 10px", borderTop:`1px solid ${C.border}` }}>
          <button onClick={onLogout} style={{ ...S.btn("glass","md"), width:"100%", justifyContent:"center", fontSize:13, color:C.textMuted }}>
            🚪 Sign Out
          </button>
        </div>
      </div>

      {/* Main */}
      <div className="lms-main" style={S.mainArea}>
        {/* Topbar */}
        <div className="lms-topbar" style={S.topbar}>
          <div style={{ display:"flex", alignItems:"center", gap:14 }}>
            <button
              className="lms-hamburger"
              onClick={() => setMobileOpen(v => !v)}
              aria-label="Toggle menu"
              style={{ background:C.surfaceAlt, border:`1px solid ${C.border}`, borderRadius:10, width:38, height:38, alignItems:"center", justifyContent:"center", cursor:"pointer", fontSize:18, color:C.text, flexShrink:0 }}>
              ☰
            </button>
            <div>
              <h1 style={{ fontSize:18, fontWeight:700, color:C.text, margin:0 }}>{LABELS[page]||"Dashboard"}</h1>
              <div style={{ fontSize:11, color:C.textMuted, marginTop:2 }}>
                {new Date().toLocaleDateString("en-PK",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}
              </div>
            </div>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:16 }}>
            {/* Fees warning */}
            {user.role==="student" && user.fees_status==="unpaid" && (
              <div style={{ padding:"6px 14px", background:C.roseLight, border:"1px solid #FCA5B1", borderRadius:8, fontSize:12, color:"#E11D48", fontWeight:600 }}>
                ❌ Fees Overdue
              </div>
            )}
            {user.role==="student" && user.fees_status==="partial" && (
              <div style={{ padding:"6px 14px", background:C.goldLight, border:"1px solid #F3D896", borderRadius:8, fontSize:12, color:C.goldDark, fontWeight:600 }}>
                ⚠️ Partial Payment
              </div>
            )}

            {/* Notifications */}
            <div style={{ position:"relative" }}>
              <button
                style={{ background:C.surfaceAlt, border:`1px solid ${C.border}`, borderRadius:10, padding:"8px 12px", cursor:"pointer", color:C.text, fontSize:16, position:"relative" }}
                onClick={() => setShowNotif(v => !v)}>
                🔔
                {unread > 0 && (
                  <span style={{ position:"absolute", top:-4, right:-4, background:"#F43F5E", color:C.white, borderRadius:"50%", width:17, height:17, fontSize:9, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:700 }}>
                    {unread > 9 ? "9+" : unread}
                  </span>
                )}
              </button>

              {/* Notification Panel */}
              {showNotif && (
                <div style={{ position:"absolute", right:0, top:52, width:360, maxHeight:"calc(100vh - 90px)", display:"flex", flexDirection:"column", background:C.surface, border:`1px solid ${C.border}`, borderRadius:16, boxShadow:"0 20px 50px rgba(15,23,42,0.15)", zIndex:300, overflow:"hidden" }}>
                  <div style={{ padding:"16px 20px", borderBottom:`1px solid ${C.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                    <span style={{ fontWeight:700, fontSize:15, color:C.text }}>
                      Notifications {unread > 0 && <span style={{ background:"#F43F5E", color:C.white, borderRadius:20, padding:"2px 8px", fontSize:11, marginLeft:6 }}>{unread}</span>}
                    </span>
                    {unread > 0 && (
                      <button onClick={markAllRead} style={{ ...S.btn("ghost","sm"), fontSize:12, color:C.textMuted }}>
                        Mark all read
                      </button>
                    )}
                  </div>
                  <div style={{ maxHeight:340, minHeight:0, overflow:"auto", WebkitOverflowScrolling:"touch", overscrollBehavior:"contain" }}>
                    {notifications.length === 0
                      ? <div style={{ padding:28, textAlign:"center", color:C.textMuted, fontSize:13 }}>No notifications</div>
                      : notifications.map(n => (
                        <div key={n.id} style={{ padding:"13px 20px", borderBottom:`1px solid ${C.border}`, background:n.is_read ? "transparent" : C.navyPale, display:"flex", gap:12 }}>
                          <span style={{ fontSize:18, marginTop:1 }}>
                            {n.type==="fees" ? "💰" : n.type==="exam" ? "📋" : n.type==="assignment" ? "📝" : "📢"}
                          </span>
                          <div style={{ flex:1 }}>
                            <div style={{ fontSize:13, color:C.textMid, lineHeight:1.5 }}>{n.message}</div>
                            <div style={{ fontSize:11, color:C.textMuted, marginTop:3 }}>
                              {new Date(n.created_at).toLocaleDateString("en-PK")}
                            </div>
                          </div>
                          {!n.is_read && <div style={{ width:8, height:8, borderRadius:"50%", background:C.navy, marginTop:5, flexShrink:0 }} />}
                        </div>
                      ))
                    }
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div style={S.content}>{children}</div>
      </div>

      {showNotif && <div style={{ position:"fixed", inset:0, zIndex:40 }} onClick={() => setShowNotif(false)} />}
      {mobileOpen && <div style={{ position:"fixed", inset:0, zIndex:90, background:"rgba(15,23,42,0.35)" }} onClick={() => setMobileOpen(false)} />}
    </div>
  );
}
